import { getCurlyBracketText } from '@/util/common'

import { Folder } from '../types'
import { defaultFolder } from './defaultFolderData'

/** 分类字段的 key，对应 categoryOptions 的 value */
export type ClassifyKey =
  | 'orderNo'
  | 'roomName'
  | 'canvasIndex'
  | 'plankWidth'
  | 'plankHeight'
  | 'matCode'
  | 'texture'
  | 'thick'
  | 'bigPlankType'

export interface ProcessFileItem {
  type: string
  name: string
  ext: string
  /**
   * 该文件不支持的分类字段，不填则支持全部分类。
   * 填 'all' 表示完全无法被任何分类所接受。
   */
  unsupportedClassify?: ClassifyKey[] | 'all'
}

/**
 * @description 默认存在后端会生成的加工文件
 *
 * 新增文件类型时，在对应分组的 files 数组追加一项即可。
 * 不填 unsupportedClassify 则默认支持全部分类。
 */
export const defaultProcessFiles: {
  type: string
  name: string
  isExpand: boolean
  files: ProcessFileItem[]
}[] = [
  {
    type: 'engraving' as const,
    name: '雕刻机' as const,
    isExpand: true,
    files: [
      {
        type: 'engraving_front' as const,
        name: '雕刻机正面文件' as const,
        ext: '.nc',
        unsupportedClassify: ['orderNo', 'roomName'] as ClassifyKey[],
      },
      {
        type: 'engraving_opposite' as const,
        name: '雕刻机反面文件' as const,
        ext: '.nc',
        unsupportedClassify: ['orderNo', 'roomName'] as ClassifyKey[],
      },
      {
        type: 'engraving_merge' as const,
        name: '雕刻机合并文件' as const,
        ext: '.nc',
        // unsupportedClassify: ['orderNo', 'roomName'] as ClassifyKey[],
        unsupportedClassify: 'all' as const,
      },
      {
        type: 'engraving_mill' as const,
        name: '二次加工牛角槽铣槽' as const,
        ext: '.nc',
      },
      {
        type: 'engraving_handle' as const,
        name: '二次加工斜边海棠角和斜边拉手文件' as const,
        ext: '.nc',
      },
      {
        type: 'engraving_arc_slot' as const,
        name: '二次加工铣底圆弧槽' as const,
        ext: '.nc',
      },
      {
        type: 'engraving_line_slot' as const,
        name: '二次加工自定义刀路' as const,
        ext: '.nc',
      },
      {
        type: 'engraving_straight_arc_slot' as const,
        name: '二次加工圆弧折弯部分直槽' as const,
        ext: '.nc',
      },
      {
        type: 'engraving_frame_door' as const,
        name: '二次加工骨骼门文件' as const,
        ext: '.nc',
      },
      {
        type: 'engraving_inner_shape' as const,
        name: '二次加工内部异形' as const,
        ext: '.nc',
      },
      {
        type: 'engraving_hole' as const,
        name: '二次加工常规孔' as const,
        ext: '.nc',
      },
      {
        type: 'engraving_slot' as const,
        name: '二次加工常规槽' as const,
        ext: '.nc',
      },
      {
        type: 'engraving_single_file_merge' as const,
        name: '二次加工合并文件' as const,
        ext: '.nc',
      },
    ],
  },
  {
    type: 'electronic' as const,
    name: '电子锯文件' as const,
    isExpand: true,
    files: [
      {
        type: 'electronic_saw' as const,
        name: '电子锯文件' as const,
        ext: '.nc',
        unsupportedClassify: [
          'canvasIndex',
          'plankWidth',
          'plankHeight',
          'bigPlankType',
        ] as ClassifyKey[],
      },
    ],
  },
  {
    type: 'glass-machine' as const,
    name: '玻璃切割机文件' as const,
    isExpand: true,
    files: [
      {
        type: 'glass' as const,
        name: '玻璃切割机文件' as const,
        ext: 'opt',
        unsupportedClassify: ['orderNo', 'roomName'] as ClassifyKey[],
      },
    ],
  },
  {
    type: 'side-hole' as const,
    name: '侧孔机' as const,
    isExpand: true,
    files: [
      {
        type: 'side_machine' as const,
        name: '侧孔机文件' as const,
        ext: '',
      },
      {
        type: 'side_machine_xml3' as const,
        name: '旧版侧孔机XML3文件' as const,
        ext: '.xml',
        unsupportedClassify: 'all' as const,
      },
      {
        type: 'side_machine_txt' as const,
        name: '旧版侧孔机TXT文件' as const,
        ext: '',
      },
      {
        type: 'side_machine_nc' as const,
        name: '旧版侧孔机nc文件' as const,
        ext: '.nc',
      },
    ],
  },
  {
    type: 'drilling' as const,
    name: '五/六面钻' as const,
    isExpand: true,
    files: [
      {
        type: 'dock56F' as const,
        name: '五/六面钻文件' as const,
        ext: '.xml',
      },
    ],
  },
  {
    type: 'ptp' as const,
    name: 'PTP' as const,
    isExpand: true,
    files: [
      {
        type: 'ptp' as const,
        name: 'PTP文件' as const,
        ext: '.nc',
      },
    ],
  },
  {
    type: 'integrated' as const,
    name: '一体机' as const,
    isExpand: true,
    files: [
      {
        type: 'aio' as const,
        name: '一体机文件' as const,
        ext: '.nc',
        unsupportedClassify: ['orderNo', 'roomName'] as ClassifyKey[],
      },
      {
        type: 'aio_anc' as const,
        name: '一体机正面anc文件' as const,
        ext: '.nc',
        unsupportedClassify: ['orderNo', 'roomName'] as ClassifyKey[],
      },
    ],
  },
  {
    type: 'door_wall_aio' as const,
    name: '门墙柜一体机' as const,
    isExpand: true,
    files: [
      {
        type: 'door_wall' as const,
        name: '门墙柜一体机' as const,
        ext: '.nc',
      },
    ],
  },
  {
    type: 'labeling' as const,
    name: '贴标机' as const,
    isExpand: true,
    files: [
      {
        type: 'labeling_1' as const,
        name: '贴标机文件1' as const,
        ext: '.xml',
        unsupportedClassify: ['orderNo', 'roomName'] as ClassifyKey[],
      },
      {
        type: 'labeling_2' as const,
        name: '贴标机文件2' as const,
        ext: '.xml',
        unsupportedClassify: ['orderNo', 'roomName'] as ClassifyKey[],
      },
      {
        type: 'labeling_3' as const,
        name: '贴标机文件3' as const,
        ext: '.xml',
        unsupportedClassify: ['orderNo', 'roomName'] as ClassifyKey[],
      },
    ],
  },
  {
    type: 'robotic_arm' as const,
    name: '机械臂' as const,
    isExpand: true,
    files: [
      {
        type: 'robotic_arm' as const,
        name: '机械臂文件' as const,
        ext: '.nc',
      },
    ],
  },
  {
    type: 'label_image' as const,
    name: '标签' as const,
    isExpand: true,
    files: [
      {
        type: 'label_image' as const,
        name: '标签图片文件' as const,
        ext: '.jpg',
        unsupportedClassify: ['orderNo', 'roomName'] as ClassifyKey[],
      },
      {
        type: 'label_pdf' as const,
        name: '标签PDF文件' as const,
        ext: '.pdf',
        unsupportedClassify: 'all' as const,
      },
      {
        type: 'paiban_image' as const,
        name: '排版图图片文件' as const,
        ext: '.jpg',
        unsupportedClassify: ['orderNo', 'roomName'] as ClassifyKey[],
      },
      {
        type: 'paiban_pdf' as const,
        name: '排版图PDF文件' as const,
        ext: '.pdf',
        unsupportedClassify: 'all' as const,
      },
    ],
  },
]

/** 标签来源类型：分类标签 / 普通标签 */
export type TagType = 'classify' | 'normal'

/** 标签项公共结构 */
export interface TagOption {
  label: string
  name: string
  value: string
  /** 标签来源 */
  type: TagType
  tooltip?: string
}

/** @description 可选的分类选项 */
export const categoryOptions: TagOption[] = [
  {
    label: '订单号',
    name: '{订单号}',
    value: '{orderNo}',
    type: 'classify',
  },
  {
    label: '房间名称',
    name: '{房间名称}',
    value: '{roomName}',
    type: 'classify',
  },
  {
    label: '每张大板',
    name: '{每张大板}',
    value: '{canvasIndex}',
    type: 'classify',
  },
  {
    label: '大板宽度',
    name: '{大板宽度}',
    value: '{plankWidth}',
    type: 'classify',
  },
  {
    label: '大板长度',
    name: '{大板长度}',
    value: '{plankHeight}',
    type: 'classify',
  },
  {
    label: '材质',
    name: '{材质}',
    value: '{matCode}',
    type: 'classify',
  },
  {
    label: '颜色',
    name: '{颜色}',
    value: '{texture}',
    type: 'classify',
  },
  {
    label: '厚度',
    name: '{厚度}',
    value: '{thick}',
    type: 'classify',
  },
  {
    label: '板件类型(余料板/常规板/特殊板)',
    name: '{余料板_常规板_特殊板}',
    value: '{bigPlankType}',
    type: 'classify',
  },
]

/** @description 获取所有的文件 */
const FILE_TYPE_REGISTRY = defaultProcessFiles.flatMap((group) => group.files)

/** @description 完全无法被任何分类所接受的文件类型 */
export const UNCLASSIFIABLE_TYPES: ReadonlySet<string> = new Set(
  FILE_TYPE_REGISTRY.filter((f) => f.unsupportedClassify === 'all').map(
    (f) => f.type
  )
)

/** @description 根据类型判断 */
function getTypesForKey(key: ClassifyKey): string[] {
  return FILE_TYPE_REGISTRY.filter((f) => {
    if (!f.unsupportedClassify) return true
    if (f.unsupportedClassify === 'all') return false
    return !f.unsupportedClassify.includes(key)
  }).map((f) => f.type)
}

/** @description 分类字段与支持的文件类型映射 */
export const CLASSIFY_SUPPORT_MAP: Record<ClassifyKey, string[]> = {
  orderNo: getTypesForKey('orderNo'),
  roomName: getTypesForKey('roomName'),
  canvasIndex: getTypesForKey('canvasIndex'),
  plankWidth: getTypesForKey('plankWidth'),
  plankHeight: getTypesForKey('plankHeight'),
  matCode: getTypesForKey('matCode'),
  texture: getTypesForKey('texture'),
  thick: getTypesForKey('thick'),
  bigPlankType: getTypesForKey('bigPlankType'),
}

/** 可选的文件夹名称填充字段
 * @param {string} field 在文件上对应的字段
 * @param {string} value 用于反向匹配字段信息
 */
export const folderNameFillFields: TagOption[] = [
  {
    label: '项目地址',
    name: '{项目地址}',
    value: '{address}',
    type: 'normal',
  },
  {
    label: '订单号',
    name: '{订单号}',
    value: '{orderNo}',
    type: 'normal',
  },
  {
    label: '排版时间',
    name: '{排版时间}',
    value: '{paibanTime}',
    type: 'normal',
  },
  {
    label: '递增序号',
    name: '{递增序号}',
    value: '{order}',
    type: 'normal',
  },
  {
    label: '输出时间',
    name: '{输出时间}',
    value: '{outputTime}',
    type: 'normal',
  },
  {
    label: '房间名称',
    name: '{房间名称}',
    value: '{roomName}',
    type: 'normal',
  },
  {
    label: '房间备注',
    name: '{房间备注}',
    value: '{remark}',
    type: 'normal',
  },
  {
    label: '生产线名称',
    name: '{生产线名称}',
    value: '{productionLineName}',
    type: 'normal',
  },
  {
    label: '大板X',
    name: '{大板X}',
    value: '{singleCanvasIndex}',
    type: 'normal',
    tooltip: '大板号，仅在精细排版下载时才会显示',
  },
  {
    label: '客户名称',
    name: '{客户名称}',
    value: '{customer_name}',
    type: 'normal',
  },
]

/** @description 获取自定义文件添加时要在大板/小板获取的数据字段 */
export function getCustomRecordKeys() {
  const k1 = folderNameFillFields.map((v) => v.value)
  const k2 = categoryOptions.map((v) => v.value)
  return [...k1, ...k2].flatMap((v) =>
    getCurlyBracketText(v).map((v) => v.replace(/[{}]/g, ''))
  )
}

/** @description 获取默认文件夹,当用户首次使用时需要使用此方法 */
export function getDefaultFolder(): Folder {
  const copy = window.$copy(defaultFolder)
  return copy
}
